/**
 * MeetingCreateForm — the "Schedule meeting" agenda builder (web).
 *
 * Phase 3 Task 3.5. Replaces the disabled-with-tooltip CTA on the per-trust
 * meetings screen (`/app/trusts/$id/meetings`) with the real form. Mirrors the
 * sibling trust-scoped create form (`/app/trusts/$id/resolutions` New
 * resolution modal): an in-route <Modal> + the already-wired mutation hook.
 *
 * Collects the fields the backend `CreateTrusteeMeetingSchema` requires:
 *   - title (1..300)
 *   - meetingType (in-person | remote | circulating-resolution | agm)
 *   - meetingDate (ISO date — <input type=date> already yields YYYY-MM-DD)
 *   - agendaItems[] (>=1; each { title, discussion } both non-empty)
 *   - optional chairUserId (a real trustee userId — never a display name)
 *
 * On submit → `useCreateTrustMeeting` (POST /v1/app/trusts/{trustId}/meetings).
 * The create endpoint may be step-up gated server-side; an `ApiError` with
 * `errorCode: 'STEP_UP_REQUIRED'` (or `isStepUpRequired`) is surfaced cleanly
 * via `mapError` — we do NOT retry-loop here (the step-up redirect flow is owned
 * elsewhere). Reusable: rendered both as an in-route modal on `meetings.tsx`
 * and standalone on the `meetings/new` route.
 *
 * Attendee / chair userIds come from the REAL trustee roster
 * (`useTrustTrustees`); only rows with an `active` status AND a `userId` are
 * selectable (spec-only roster rows sign / attend via the external flow).
 */

import { useEffect, useMemo, useState } from 'react';
import { displayPersonName } from '../../lib/format/person-name';
import { Modal } from '../ui/modal';
import { Icon } from '../icon';
import { useToastStore } from '../../lib/toast-store';
import { mapError } from '../../lib/error-messages';
import {
  useCreateTrustMeeting,
  useTrustTrustees,
  type TrustMeetingType,
  type CreateTrustMeetingAgendaItem,
} from '../../lib/api';

const MEETING_TYPES: { id: TrustMeetingType; label: string }[] = [
  { id: 'in-person', label: 'In person' },
  { id: 'remote', label: 'Remote' },
  { id: 'agm', label: 'AGM' },
  { id: 'circulating-resolution', label: 'Circulating resolution' },
];

interface AgendaRow {
  /** Local-only stable key for the row (never sent to the backend). */
  key: string;
  title: string;
  discussion: string;
}

let agendaKeySeq = 0;
function newAgendaRow(): AgendaRow {
  agendaKeySeq += 1;
  return { key: `agenda-${agendaKeySeq}`, title: '', discussion: '' };
}

const labelStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-3)',
  display: 'block',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

export interface MeetingCreateFormProps {
  trustId: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful create (e.g. to refetch / switch tabs). The
   *  per-trust meetings query is already invalidated by the mutation hook. */
  onCreated?: () => void;
}

export function MeetingCreateForm({ trustId, open, onClose, onCreated }: MeetingCreateFormProps) {
  const pushToast = useToastStore((s) => s.pushToast);
  const createMut = useCreateTrustMeeting();
  const trusteesQ = useTrustTrustees(trustId);

  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState<TrustMeetingType>('in-person');
  const [meetingDate, setMeetingDate] = useState(''); // YYYY-MM-DD from <input type=date>
  const [agenda, setAgenda] = useState<AgendaRow[]>([newAgendaRow()]);
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [attendeesTouched, setAttendeesTouched] = useState(false);
  const [chairUserId, setChairUserId] = useState(''); // '' = let the backend default

  // Roster rows that can be submitted (active + carry a real Supabase userId).
  const attendeeRows = useMemo(
    () =>
      (trusteesQ.data?.trustees ?? [])
        .filter((t) => t.status === 'active' && !!t.userId)
        .map((t) => ({ userId: t.userId!, displayName: t.displayName })),
    [trusteesQ.data?.trustees],
  );

  // Default attendees to every active trustee until the user touches the
  // picker (mirrors the resolutions default-signers behaviour).
  useEffect(() => {
    if (attendeesTouched || attendeeRows.length === 0) return;
    setSelectedAttendees(attendeeRows.map((t) => t.userId));
  }, [attendeeRows, attendeesTouched]);

  function resetForm() {
    setTitle('');
    setMeetingType('in-person');
    setMeetingDate('');
    setAgenda([newAgendaRow()]);
    setSelectedAttendees([]);
    setAttendeesTouched(false);
    setChairUserId('');
  }

  function toggleAttendee(userId: string) {
    setAttendeesTouched(true);
    setSelectedAttendees((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  function updateAgenda(key: string, patch: Partial<Pick<AgendaRow, 'title' | 'discussion'>>) {
    setAgenda((cur) => cur.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  }

  function addAgendaRow() {
    setAgenda((cur) => [...cur, newAgendaRow()]);
  }

  function removeAgendaRow(key: string) {
    setAgenda((cur) => (cur.length <= 1 ? cur : cur.filter((a) => a.key !== key)));
  }

  function handleSubmit() {
    if (createMut.isPending) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      pushToast({ kind: 'error', message: 'Give the meeting a title before scheduling.' });
      return;
    }
    if (!meetingDate) {
      pushToast({ kind: 'error', message: 'Pick a meeting date before scheduling.' });
      return;
    }
    // The backend stores each agenda item with BOTH a title and a discussion
    // (its schema requires both non-empty). Drop fully-blank rows, then validate
    // that at least one COMPLETE item remains.
    const agendaItems: CreateTrustMeetingAgendaItem[] = agenda
      .map((a) => ({ title: a.title.trim(), discussion: a.discussion.trim() }))
      .filter((a) => a.title.length > 0 || a.discussion.length > 0);
    if (agendaItems.length === 0) {
      pushToast({ kind: 'error', message: 'Add at least one agenda item to schedule a meeting.' });
      return;
    }
    if (agendaItems.some((a) => a.title.length === 0 || a.discussion.length === 0)) {
      pushToast({
        kind: 'error',
        message: 'Every agenda item needs both a heading and a discussion note.',
      });
      return;
    }

    createMut.mutate(
      {
        trustId,
        title: trimmedTitle,
        meetingType,
        meetingDate, // already YYYY-MM-DD
        agendaItems,
        // Chair is optional; submit the chosen trustee's REAL userId only when
        // the user picked one AND they're an attendee.
        ...(chairUserId && selectedAttendees.includes(chairUserId)
          ? { chairUserId }
          : {}),
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
          onCreated?.();
          pushToast({ kind: 'success', message: 'Meeting scheduled.' });
        },
        onError: (err) => {
          // mapError surfaces STEP_UP_REQUIRED with its own copy; never the
          // raw infra message.
          pushToast({ kind: 'error', message: mapError(err, 'save') });
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Schedule meeting" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 4 }}>
        <div>
          <label htmlFor="mtg-title" style={labelStyle}>
            Meeting title
          </label>
          <input
            id="mtg-title"
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Q3 2026 trustees meeting"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="mtg-type" style={labelStyle}>
              Meeting type
            </label>
            <select
              id="mtg-type"
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value as TrustMeetingType)}
              style={inputStyle}
            >
              {MEETING_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="mtg-date" style={labelStyle}>
              Date
            </label>
            <input
              id="mtg-date"
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Attendees — real trustee userIds. */}
        <div>
          <label style={labelStyle}>Attendees</label>
          {attendeeRows.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                background: 'var(--surface-2)',
                padding: '10px 12px',
                borderRadius: 8,
              }}
            >
              No trustees with app access are available for this trust yet. The
              meeting will be recorded against your account; add attendees once
              the trust&rsquo;s roster is fully lodged.
            </div>
          ) : (
            <div
              role="group"
              aria-label="Attendees"
              style={{
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                padding: '10px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {attendeeRows.map((t) => {
                const checked = selectedAttendees.includes(t.userId);
                const isChair = chairUserId === t.userId;
                return (
                  <div
                    key={t.userId}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 13.5,
                        cursor: 'pointer',
                        flex: 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAttendee(t.userId)}
                      />
                      {displayPersonName(t.displayName)}
                    </label>
                    <button
                      type="button"
                      onClick={() => setChairUserId(isChair ? '' : t.userId)}
                      disabled={!checked}
                      title={checked ? '' : 'Add as attendee first'}
                      style={{
                        background: isChair ? 'var(--primary-700)' : 'var(--surface-2)',
                        color: isChair ? 'var(--surface)' : 'var(--ink-3)',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: checked ? 'pointer' : 'not-allowed',
                        opacity: checked ? 1 : 0.5,
                      }}
                    >
                      {isChair ? 'Chair' : 'Set chair'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Agenda builder — add/remove rows; each needs heading + discussion. */}
        <div>
          <label style={labelStyle}>Agenda</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {agenda.map((row, idx) => (
              <div
                key={row.key}
                style={{
                  border: '1px solid var(--hairline)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  background: 'var(--surface)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    className="eyebrow ink"
                  >
                    Item {idx + 1}
                  </span>
                  {agenda.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAgendaRow(row.key)}
                      aria-label={`Remove agenda item ${idx + 1}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--ink-3)',
                        cursor: 'pointer',
                        fontSize: 12.5,
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        padding: 0,
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={row.title}
                  onChange={(e) => updateAgenda(row.key, { title: e.target.value })}
                  placeholder="Heading, e.g. Approve annual financial statements"
                  aria-label={`Agenda item ${idx + 1} heading`}
                  style={inputStyle}
                />
                <textarea
                  value={row.discussion}
                  onChange={(e) => updateAgenda(row.key, { discussion: e.target.value })}
                  rows={2}
                  placeholder="Short discussion note for the minutes"
                  aria-label={`Agenda item ${idx + 1} discussion`}
                  style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addAgendaRow}
            style={{
              marginTop: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              color: 'var(--primary-700)',
              cursor: 'pointer',
              fontFamily: 'var(--sans)',
              fontSize: 13.5,
              fontWeight: 500,
              padding: 0,
            }}
          >
            <Icon name="plus" size={14} stroke={2} /> Add agenda item
          </button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          The meeting is saved as a draft. Open it from the meetings list to
          minute and finalise it.
        </div>
      </div>
      <Modal.Footer>
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!title.trim() || !meetingDate || createMut.isPending}
        >
          {createMut.isPending ? 'Scheduling…' : 'Schedule meeting'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
